import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI, ClinicianRegisterRequest } from '../../services/api';
import toast from 'react-hot-toast';
import Button from '../ui/Button';
import FormField, { Input, Textarea, Checkbox } from '../ui/FormField';
import StatusBanner from '../ui/StatusBanner';
import {
  EnvelopeIcon,
  LockClosedIcon,
  UserIcon,
  PhoneIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  AcademicCapIcon,
  IdentificationIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const ClinicianRegister: React.FC = () => {
  const [formData, setFormData] = useState<Omit<ClinicianRegisterRequest, 'tos_accepted'>>({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    specialty: '',
    license_number: '',
    phone: '',
    profile_bio: '',
    consent_given: false,
  });
  const [tosAccepted, setTosAccepted] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Password strength checks
  const passwordStrength = {
    hasMinLength: formData.password.length >= 8,
    hasUppercase: /[A-Z]/.test(formData.password),
    hasLowercase: /[a-z]/.test(formData.password),
    hasNumber: /[0-9]/.test(formData.password),
  };
  const isPasswordStrong = Object.values(passwordStrength).every(Boolean);

  const validateForm = (): string | null => {
    if (!isPasswordStrong) {
      return 'Password does not meet requirements';
    }
    if (formData.password !== confirmPassword) {
      return 'Passwords do not match';
    }
    if (!formData.consent_given) {
      return 'You must consent to the privacy policy to register';
    }
    if (!tosAccepted) {
      return 'You must accept the Terms of Service to register';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);

    try {
      await authAPI.registerClinician({
        ...formData,
        tos_accepted: tosAccepted,
      });
      setIsSuccess(true);
      toast.success('Registration submitted!');
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map((d: any) => d.msg).join(', '));
      } else {
        setError(detail || 'Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Success state
  if (isSuccess) {
    return (
      <div className="min-h-screen flex">
        {/* Left Side - Brand Visual */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-clinic-cream via-clinic-cream-light to-clinic-gold/10 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-clinic-gold/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-clinic-primary/10 rounded-full translate-y-1/2 -translate-x-1/2" />

          <div className="relative z-10 flex flex-col justify-center px-12 lg:px-20">
            <img
              src="/logos/logo-primary.png"
              alt="Clinic Studios"
              className="h-14 w-auto mb-10 object-contain object-left"
            />
            <h1 className="text-3xl lg:text-4xl font-bold text-clinic-dark font-body leading-tight mb-6">
              Thank you for
              <br />
              <span className="text-clinic-gold">joining us</span>
            </h1>
            <p className="text-base text-clinic-dark/70 max-w-sm leading-relaxed">
              Your application is now being reviewed by our team.
            </p>
          </div>
        </div>

        {/* Right Side - Success Message */}
        <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 bg-white">
          <div className="w-full max-w-md text-center">
            <div className="lg:hidden mb-8">
              <img
                src="/logos/logo-primary.png"
                alt="Clinic Studios"
                className="h-12 w-auto mx-auto"
              />
            </div>

            <div className="bg-clinic-primary/10 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
              <CheckCircleIcon className="w-10 h-10 text-clinic-primary" />
            </div>

            <h2 className="text-2xl font-bold text-clinic-dark font-body mb-4">
              Registration Submitted!
            </h2>

            <div className="bg-clinic-cream/50 rounded-card p-6 mb-6 text-left">
              <h3 className="font-semibold text-clinic-dark mb-3 flex items-center">
                <ClockIcon className="w-5 h-5 mr-2 text-clinic-gold" />
                What happens next?
              </h3>
              <ul className="space-y-2 text-sm text-clinic-muted">
                <li className="flex items-start">
                  <span className="w-6 h-6 rounded-full bg-clinic-primary/20 text-clinic-primary text-xs flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">1</span>
                  Our team will review your credentials
                </li>
                <li className="flex items-start">
                  <span className="w-6 h-6 rounded-full bg-clinic-primary/20 text-clinic-primary text-xs flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">2</span>
                  You'll receive an email once approved
                </li>
                <li className="flex items-start">
                  <span className="w-6 h-6 rounded-full bg-clinic-primary/20 text-clinic-primary text-xs flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">3</span>
                  Log in and set up your profile
                </li>
              </ul>
              <p className="text-xs text-clinic-muted mt-4 pt-3 border-t border-clinic-primary-lighter">
                This typically takes 1-2 business days.
              </p>
            </div>

            <Link
              to="/login"
              className="text-clinic-primary hover:underline font-medium"
            >
              Return to login page
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Brand Visual */}
      <div className="hidden lg:flex lg:w-5/12 bg-gradient-to-br from-clinic-cream via-clinic-cream-light to-clinic-gold/10 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-clinic-gold/10 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-clinic-primary/10 rounded-full translate-y-1/2 -translate-x-1/2" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 lg:px-16">
          {/* Logo */}
          <img
            src="/logos/logo-primary.png"
            alt="Clinic Studios"
            className="h-14 w-auto mb-10 object-contain object-left"
          />

          {/* Tagline */}
          <h1 className="text-3xl lg:text-4xl font-bold text-clinic-dark font-body leading-tight mb-6">
            Grow your
            <br />
            <span className="text-clinic-gold">practice with us</span>
          </h1>

          <p className="text-base text-clinic-dark/70 max-w-sm mb-8 leading-relaxed">
            Join our community of trusted mental health professionals and connect with patients seeking care.
          </p>

          {/* Benefits */}
          <div className="space-y-3">
            {[
              'Flexible room booking',
              'Online appointment management',
              'Build your client base',
              'Transparent billing',
            ].map((feature, index) => (
              <div key={index} className="flex items-center text-clinic-dark/70">
                <CheckCircleIcon className="w-5 h-5 text-clinic-gold mr-3" />
                <span className="text-sm">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom decoration */}
        <div className="absolute bottom-8 left-12 lg:left-16">
          <img
            src="/logos/logo-icon.png"
            alt=""
            className="h-8 w-auto opacity-30"
          />
        </div>
      </div>

      {/* Right Side - Registration Form */}
      <div className="w-full lg:w-7/12 flex items-start justify-center px-6 py-6 bg-white overflow-y-auto">
        <div className="w-full max-w-lg">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-4 text-center">
            <img
              src="/logos/logo-primary.png"
              alt="Clinic Studios"
              className="h-10 w-auto mx-auto mb-2"
            />
          </div>

          {/* Header */}
          <div className="mb-4">
            <h2 className="text-2xl font-bold text-clinic-dark font-body">
              Clinician Registration
            </h2>
            <p className="mt-1 text-clinic-muted text-sm">
              Join Clinic Studios as a mental health professional
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4">
              <StatusBanner
                variant="error"
                title="Registration Error"
                message={error}
                dismissible
                onDismiss={() => setError('')}
              />
            </div>
          )}

          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-3">
              <FormField label="First Name" htmlFor="first_name" required>
                <Input
                  id="first_name"
                  name="first_name"
                  type="text"
                  autoComplete="given-name"
                  placeholder="First name"
                  maxLength={100}
                  value={formData.first_name}
                  onChange={handleChange}
                  leftIcon={<UserIcon className="h-5 w-5" />}
                  required
                />
              </FormField>
              <FormField label="Last Name" htmlFor="last_name" required>
                <Input
                  id="last_name"
                  name="last_name"
                  type="text"
                  autoComplete="family-name"
                  placeholder="Last name"
                  maxLength={100}
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                />
              </FormField>
            </div>

            <FormField label="Email Address" htmlFor="email" required>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                leftIcon={<EnvelopeIcon className="h-5 w-5" />}
                required
              />
            </FormField>

            <div className="grid grid-cols-2 gap-3">
              <FormField label="Professional Title" htmlFor="specialty">
                <Input
                  id="specialty"
                  name="specialty"
                  type="text"
                  placeholder="e.g., Psychologist, Counsellor"
                  maxLength={100}
                  value={formData.specialty}
                  onChange={handleChange}
                  leftIcon={<AcademicCapIcon className="h-5 w-5" />}
                  required
                />
              </FormField>
              <FormField label="License Number" htmlFor="license_number">
                <Input
                  id="license_number"
                  name="license_number"
                  type="text"
                  placeholder="License #"
                  maxLength={50}
                  value={formData.license_number}
                  onChange={handleChange}
                  leftIcon={<IdentificationIcon className="h-5 w-5" />}
                  
                />
              </FormField>
            </div>

            <FormField label="Phone Number" htmlFor="phone">
              <Input
                id="phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                placeholder="(555) 123-4567"
                maxLength={20}
                value={formData.phone}
                onChange={handleChange}
                leftIcon={<PhoneIcon className="h-5 w-5" />}
              />
            </FormField>

            <FormField label="Profile Bio (may be publicly visible)" htmlFor="profile_bio" helper={`${formData.profile_bio?.length || 0}/2000 characters`}>
              <Textarea
                id="profile_bio"
                name="profile_bio"
                placeholder="Tell potential clients about your practice, approach, and areas of expertise..."
                rows={3}
                maxLength={2000}
                value={formData.profile_bio}
                onChange={handleChange}
              />
            </FormField>

            <div className="grid grid-cols-2 gap-3">
              <FormField label="Password" htmlFor="password" required>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  placeholder="Password"
                  minLength={8}
                  value={formData.password}
                  onChange={handleChange}
                  leftIcon={<LockClosedIcon className="h-5 w-5" />}
                  required
                />
              </FormField>
              <FormField label="Confirm Password" htmlFor="confirmPassword" required>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  placeholder="Confirm"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  leftIcon={<LockClosedIcon className="h-5 w-5" />}
                  error={Boolean(confirmPassword && formData.password !== confirmPassword)}
                  required
                />
              </FormField>
            </div>

            {/* Password Strength Indicators */}
            {formData.password && (
              <div className="grid grid-cols-4 gap-1 text-xs">
                <span className={passwordStrength.hasMinLength ? 'text-clinic-primary' : 'text-clinic-muted'}>
                  {passwordStrength.hasMinLength ? '✓' : '○'} 8+ chars
                </span>
                <span className={passwordStrength.hasUppercase ? 'text-clinic-primary' : 'text-clinic-muted'}>
                  {passwordStrength.hasUppercase ? '✓' : '○'} Upper
                </span>
                <span className={passwordStrength.hasLowercase ? 'text-clinic-primary' : 'text-clinic-muted'}>
                  {passwordStrength.hasLowercase ? '✓' : '○'} Lower
                </span>
                <span className={passwordStrength.hasNumber ? 'text-clinic-primary' : 'text-clinic-muted'}>
                  {passwordStrength.hasNumber ? '✓' : '○'} Number
                </span>
              </div>
            )}

            {/* PIPEDA Consent */}
            <div className="bg-clinic-cream/50 p-3 rounded-card border border-clinic-primary-lighter">
              <Checkbox
                id="consent_given"
                name="consent_given"
                checked={formData.consent_given}
                onChange={handleChange}
                label={
                  <span className="text-sm text-clinic-dark">
                    <strong>Privacy Policy Consent</strong>
                    <p className="font-normal text-clinic-muted mt-0.5 text-xs">
                      I consent to Clinic Studios collecting and using my information per the{' '}
                      <Link to="/privacy" target="_blank" className="text-clinic-primary hover:underline">
                        Privacy Policy
                      </Link>
                      . I understand my profile may be displayed publicly.
                    </p>
                  </span>
                }
                required
              />
            </div>

            {/* Terms of Service */}
            <div className="bg-clinic-cream/50 p-3 rounded-card border border-clinic-primary-lighter">
              <Checkbox
                id="tos_accepted"
                name="tos_accepted"
                checked={tosAccepted}
                onChange={(e) => setTosAccepted(e.target.checked)}
                label={
                  <span className="text-sm text-clinic-dark">
                    <strong>Terms of Service</strong>
                    <p className="font-normal text-clinic-muted mt-0.5 text-xs">
                      I agree to the{' '}
                      <Link to="/terms" target="_blank" className="text-clinic-primary hover:underline">
                        Terms of Service
                      </Link>
                      , including booking policies, payment terms, and clinician responsibilities.
                    </p>
                  </span>
                }
                required
              />
            </div>

            <Button
              type="submit"
              fullWidth
              size="lg"
              isLoading={isLoading}
              rightIcon={!isLoading && <ArrowRightIcon className="h-5 w-5" />}
            >
              {isLoading ? 'Submitting...' : 'Submit Registration'}
            </Button>
          </form>

          {/* Footer Links */}
          <div className="mt-4 text-center space-y-1 pb-4">
            <p className="text-sm text-clinic-muted">
              Already have an account?{' '}
              <Link to="/login" className="text-clinic-primary hover:underline font-medium">
                Sign in
              </Link>
            </p>
            <p className="text-sm text-clinic-muted">
              Are you a patient?{' '}
              <Link to="/register/patient" className="text-clinic-primary hover:underline font-medium">
                Register as a patient
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClinicianRegister;
